/**
 * Rapsodo source descriptor — feeds into ingestParsedSource (src/db/ingest.js).
 *
 * Per-source quirks live here; orchestration (parse → dedup → archive →
 * upload row → data rows → cleanup) lives in ingest.js.
 *
 * Replaces the old src/features/rapsodo/upload.js. The orchestration that
 * file used to perform now happens in ingest.js, configured by this descriptor.
 */

import { parseRapsodoCsv, extractSessionId } from './column-map.js';

export const rapsodoSource = {
  // ----- Parsing -----
  parse(csvText) {
    const result = parseRapsodoCsv(csvText);
    const firstPitch = result.pitches[0];
    return {
      ...result,
      rows: result.pitches,
      // Rapsodo emits Pitcher First Name / Pitcher Last Name on every pitch.
      // We assume a CSV represents a single pitcher (matches today's behavior).
      athleteName: firstPitch
        ? { first: firstPitch.pitcher_first, last: firstPitch.pitcher_last }
        : null,
    };
  },

  // Filename-first, content-fallback dedup. Auto-generated Rapsodo exports
  // have a 24-hex MongoDB ObjectId in the filename. Coach-renamed exports
  // ("2026-05-10_bullpen_pt_2.csv") don't — fall back to the first PitchID
  // (globally unique in Rapsodo's system) prefixed with "pid:" to distinguish
  // from natural ObjectIds. Same session uploaded twice with different
  // renames will still dedup because the first PitchID is content-stable.
  extractDedupKey({ filename, parseResult }) {
    const fromFilename = extractSessionId(filename);
    if (fromFilename) return fromFilename;
    const firstPitchId = parseResult?.rows?.[0]?.rapsodo_pitch_id;
    if (firstPitchId) return `pid:${firstPitchId}`;
    return null;
  },

  // ----- Schema constants -----
  archiveSource: 'rapsodo_csv',
  uploadTable: 'rapsodo_uploads',
  dedupColumn: 'rapsodo_session_id',
  dataTable: 'rapsodo_pitches',

  // ----- Row builders -----
  buildUploadRow({ athlete, dedupKey, parseResult, filename, storagePath }) {
    const lastPitchTime = parseResult.rows[parseResult.rows.length - 1].thrown_at;
    return {
      rapsodo_session_id: dedupKey,
      title: filename,
      athlete_id: athlete.id,
      file_path: storagePath,
      row_count: parseResult.rows.length,
      uploaded_at: lastPitchTime,
    };
  },

  buildDataRows({ uploadId, athlete, parseResult }) {
    return parseResult.rows.map((p) => ({
      upload_id: uploadId,
      athlete_id: athlete.id,
      pitcher_first: p.pitcher_first,
      pitcher_last: p.pitcher_last,
      rapsodo_pitch_id: p.rapsodo_pitch_id,
      game_pitch_num: p.game_pitch_num,
      thrown_at: p.thrown_at,
      pitch_type: p.pitch_type,
      is_strike: p.is_strike,
      intent_type: p.intent_type,
      plate_loc_side_in: p.plate_loc_side_in,
      plate_loc_height_in: p.plate_loc_height_in,
      rel_speed_mph: p.rel_speed_mph,
      total_spin_rpm: p.total_spin_rpm,
      true_spin_rpm: p.true_spin_rpm,
      spin_eff_pct: p.spin_eff_pct,
      spin_dir_clock: p.spin_dir_clock,
      ind_vert_break_in: p.ind_vert_break_in,
      horz_break_in: p.horz_break_in,
      horz_rel_angle: p.horz_rel_angle,
      vert_rel_angle: p.vert_rel_angle,
      rel_height_ft: p.rel_height_ft,
      rel_side_ft: p.rel_side_ft,
      gyro_deg: p.gyro_deg,
      ssw_vert_ft: p.ssw_vert_ft,
      ssw_horz_ft: p.ssw_horz_ft,
      vaa: p.vaa,
      haa: p.haa,
      flagged: p.flagged,
      device_serial: p.device_serial,
      raw: p.raw,
    }));
  },

  // ----- Status payload extras -----
  buildEmptyExtras({ parseResult }) {
    return { pitchCount: parseResult.rows.length };
  },

  buildSuccessExtras({ parseResult }) {
    return {
      pitchCount: parseResult.rows.length,
      filteredCount: parseResult.filteredCount,
      unknownColumns: parseResult.unknownColumns,
    };
  },
};
